import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";

import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Undo,
  Redo,
  Link2,
  AlignLeft,
  AlignCenter,
  AlignRight,
} from "lucide-react";
import { useState } from "react";
import prettier from "prettier/standalone";
import htmlParser from "prettier/plugins/html";

export const formatHtml = async(html) => {
  try {
    return prettier.format(html, {
      parser: "html",
      plugins: [htmlParser],
      tabWidth: 2,
      printWidth: 80,
      htmlWhitespaceSensitivity: "ignore",
    });
  } catch (err) {
    console.error("HTML format error", err);
    return html;
  }
};


const TipTapEditor = ({ value, onChange }) => {
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [showHtml, setShowHtml] = useState(false);
  const [htmlValue, setHtmlValue] = useState("");

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
     Link.configure({
  openOnClick: true,
  autolink: true,
  linkOnPaste: true,
  HTMLAttributes: {
    target: "_blank",
    rel: "noopener noreferrer nofollow",
  },
}),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Placeholder.configure({
        placeholder: "Write policy content here...",
      }),
    ],
    content: value,
    onUpdate({ editor }) {
      onChange(editor.getHTML());
    },
  });

  if (!editor) return null;

  const Btn = ({ onClick, active, disabled, children }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`p-2 rounded hover:bg-gray-200 ${
        active ? "bg-gray-300" : ""
      } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
    >
      {children}
    </button>
  );

  return (
    <div className="border rounded-md bg-white">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 border-b bg-gray-50 p-2">
        <Btn
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 1 }).run()
          }
          active={editor.isActive("heading", { level: 1 })}
        >
          <Heading1 size={16} />
        </Btn>

        <Btn
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
          active={editor.isActive("heading", { level: 2 })}
        >
          <Heading2 size={16} />
        </Btn>

        <div className="w-px h-5 bg-gray-300 mx-1" />

        <Btn
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive("bold")}
        >
          <Bold size={16} />
        </Btn>

        <Btn
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive("italic")}
        >
          <Italic size={16} />
        </Btn>

        <Btn
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          active={editor.isActive("underline")}
        >
          <UnderlineIcon size={16} />
        </Btn>

        <div className="w-px h-5 bg-gray-300 mx-1" />

        <Btn
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive("bulletList")}
        >
          <List size={16} />
        </Btn>

        <Btn
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive("orderedList")}
        >
          <ListOrdered size={16} />
        </Btn>

        <div className="w-px h-5 bg-gray-300 mx-1" />

        <Btn onClick={() => editor.chain().focus().setTextAlign("left").run()}>
          <AlignLeft size={16} />
        </Btn>
        <Btn
          onClick={() => editor.chain().focus().setTextAlign("center").run()}
        >
          <AlignCenter size={16} />
        </Btn>
        <Btn onClick={() => editor.chain().focus().setTextAlign("right").run()}>
          <AlignRight size={16} />
        </Btn>

        <div className="w-px h-5 bg-gray-300 mx-1" />

        <Btn
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
        >
          <Undo size={16} />
        </Btn>

        <Btn
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
        >
          <Redo size={16} />
        </Btn>
        <Btn
          onClick={() => {
            setShowLinkInput(true);
            setLinkUrl(editor.getAttributes("link").href || "");
          }}
          active={editor.isActive("link")}
        >
          <Link2 size={16} />
        </Btn>
        <Btn
          onClick={async() => {
            if (!showHtml) {
              // switch to HTML view
               const formatted =  await formatHtml(editor.getHTML());
                setHtmlValue(formatted);
            } else {
              // switch back to editor (apply HTML)
              editor.commands.setContent(htmlValue);
            }
            setShowHtml(!showHtml);
          }}
          active={showHtml}
        >
          {"</>"}
        </Btn>
      </div>
      {showLinkInput && (
        <div className="flex gap-2 border-b p-2 bg-gray-100">
          <input
            type="text"
            placeholder="Paste link and press Enter"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                let url = linkUrl.trim();
                // ✅ Ensure absolute URL
                if (!url.startsWith("http://") && !url.startsWith("https://")) {
                  url = "https://" + url;
                }

                console.log("Final URL:", url); // debug
                editor.chain().focus().setLink({ href: url }).run();
                setShowLinkInput(false);
                setLinkUrl("");
              }
            }}
            className="flex-1 px-2 py-1 border rounded"
          />

          <button
            type="button"
            onClick={() => {
              editor.chain().focus().unsetLink().run();
              setShowLinkInput(false);
            }}
            className="text-sm text-red-600"
          >
            Remove
          </button>
        </div>
      )}

      {/* Editor */}
      {showHtml ? (
        <textarea
          value={htmlValue}
          onChange={(e) => {
            
          const value = e.target.value;
          setHtmlValue(value);

    // 🔥 keep editor in sync
    editor.commands.setContent(value, false);}}
          className="w-full min-h-[200px] p-3 font-mono text-sm bg-gray-50 border-none outline-none"
        />
      ) : (
        <EditorContent editor={editor} className="p-3 min-h-[200px]" />
      )}
    </div>
  );
};

export default TipTapEditor;
